import { useState, useEffect } from 'react';
import { Button, Flex, message, Typography, Upload } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
	useLazyGenerateFaiReportQuery,
	useLazyExportCsvQuery,
	useLazyGenerateBalloonedPdfQuery,
	useUploadFaiCsvMutation,
	useQueueGenerateBalloonedPdfMutation,
	useGetBallooningPdfTaskStatusQuery,
} from '../../redux/api/order-line-item';
import MultiViewUploadModal from '../../components/Operations/Quality/KeyenceFileUpload';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { getInspectionProjectId } from '../../utils/inspectionProjectId';

const { Text } = Typography;

interface Props {
	lineItem: any;
	isDisabled?: boolean;
    uploadDisabled?: boolean;
	refetchOrder?: () => void;
}

type RootState = {
	user: {
		authToken: string;
	};
};

export const QualityButtons = (props: Props) => {
	const [messageApi, contextHolder] = message.useMessage();
	const token = useSelector((state: RootState) => state.user.authToken);

	const [isLoadingLocal, setIsLoadingLocal] = useState(false);
	const [isBalloningPdfLoading, setIsBalloningPdfLoading] = useState(false);
	const [csvUploading, setCsvUploading] = useState(false);
	const history = useNavigate();
	const [isKeyenceModalOpen, setIsKeyenceModalOpen] = useState(false);

	const [
		triggerFaiReport,
		{ data: faiData, error: faiError, isLoading: isFaiLoading },
	] = useLazyGenerateFaiReportQuery();

	const [
		triggerCsvExport,
		{ data: csvData, error: csvError, isLoading: isCsvLoading },
	] = useLazyExportCsvQuery();


	const [
		triggerBalloonPdf,
		{ data: pdfData, error: pdfError, isLoading: isPdfLoading },
	] = useLazyGenerateBalloonedPdfQuery();

	const [queueBalloonPdf] = useQueueGenerateBalloonedPdfMutation();

	const [taskId, setTaskId] = useState<string | null>(null);
	const taskStatus = useGetBallooningPdfTaskStatusQuery(
		{ task_id: taskId as string },
		{ skip: !taskId, pollingInterval: 2000 }
	);

	const [uploadFaiCsv] = useUploadFaiCsvMutation();

	useEffect(() => {
		if (faiData) {
			handleFile(faiData, `FAI_Report_${props.lineItem.id}.pdf`, true);
		}
	}, [faiData]);

	useEffect(() => {
		if (csvData) {
			handleFile(csvData, `FAI_CSV_${props.lineItem.id}.csv`);
		}
	}, [csvData]);

	useEffect(() => {
		if (pdfData) {
			handleFile(pdfData, `Ballooned_${props.lineItem.id}.pdf`, true);
		}
	}, [pdfData]);

	useEffect(() => {
		if (taskId && taskStatus.data?.finished) {
			setTaskId(null);
			if (taskStatus.data.state === 'SUCCESS') {
				triggerBalloonPdf(props.lineItem.id);
				setIsBalloningPdfLoading(false);
			} else {
				messageApi.error('Failed to generate ballooned PDF');
				setIsBalloningPdfLoading(false);
			}
		}
	}, [taskId, taskStatus.data]);

	const handleFile = (blob: Blob, filename: string, isPdf = false) => {
		const url = URL.createObjectURL(blob);

		if (isPdf) {
			window.open(url, '_blank');
			setTimeout(() => URL.revokeObjectURL(url), 60000);
		} else {
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', filename);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		}
	};

	const handleDownloadFaiReport = async () => {
		setIsLoadingLocal(true);
		try {
			await triggerFaiReport(props.lineItem.id).unwrap();
		} catch (error) {
			console.error('Error generating FAI report:', error);
			messageApi.error('Failed to generate FAI report');
		} finally {
			setIsLoadingLocal(false);
		}
	};

	const handleCSVExport = async () => {
		try {
			await triggerCsvExport(props.lineItem.id).unwrap();
		} catch (error) {
			console.error('Error exporting CSV:', error);
			messageApi.error('Failed to export CSV');
		}
	};

	const handleDownloadBallooningImages = async () => {
		setIsBalloningPdfLoading(true);
		try {
			await triggerBalloonPdf(props.lineItem.id).unwrap();
			setIsBalloningPdfLoading(false);
			return;
		} catch (error: any) {
			const status = error?.status || error?.originalStatus;
			if (status === 404) {
				try {
					const res = await queueBalloonPdf({ id: props.lineItem.id }).unwrap();
					if (res?.task_id) {
						setTaskId(res.task_id);
						return;
					}
				} catch (e) {
					messageApi.error('Failed to queue ballooned PDF generation');
					setIsBalloningPdfLoading(false);
				}
			} else {
				console.error('Error generating ballooned PDF:', error);
				messageApi.error('Failed to generate ballooned PDF');
				setIsBalloningPdfLoading(false);
			}
		}
	};

	const handleUploadFaiCSV = async (file: File) => {
		setCsvUploading(true);
		const formData = new FormData();
		formData.append('file', file);

		try {
			await uploadFaiCsv({
				id: props.lineItem.id,
				file: formData,
			}).unwrap();

			messageApi.success('CSV uploaded successfully!');
			props.refetchOrder?.();
		} catch (error: any) {
			const errorMsg = error.data?.message || 'Failed to upload CSV';
			messageApi.error(errorMsg);
			console.error('CSV upload error:', error);
		} finally {
			setCsvUploading(false);
		}
	};

	// Estados combinados para los botones
	const faiLoading = isLoadingLocal || isFaiLoading;
	const balloonLoading = isBalloningPdfLoading || isPdfLoading || !!taskId;
	const showPdfError = !!pdfError && !balloonLoading && !pdfData;
	const csvExportLoading = isCsvLoading;

	return (
		<>
			<Flex vertical className="gap-2">
				<Flex wrap="wrap" className="gap-4">
					{/* Sección Ballooned File */}
					<Flex vertical align="start">
						<Text>Ballooned File</Text>
							<Button
								icon={<DownloadOutlined />}
								type="primary"
								onClick={handleDownloadBallooningImages}
								loading={balloonLoading}
								disabled={props.isDisabled}
							>
								{balloonLoading ? 'Generating...' : 'Download'}
							</Button>
							{showPdfError && (
								<Text type="danger" style={{ marginTop: 4 }}>
									Error generating PDF
								</Text>
							)}
					</Flex>
					{getInspectionProjectId(props.lineItem) && (
						<Flex vertical align="start">
							<Text>Ballooning Project</Text>
							<Button
								type="primary"
								onClick={() => history(`/ballooning/${getInspectionProjectId(props.lineItem)}`)}
							// disabled={props.isDisabled}
							>
								Go to Project
							</Button>
						</Flex>
					)}
					{/*keyence*/}
					{props.lineItem?.is_keyence_program_required && (
						<Flex vertical align="start">
							<Text>Keyence</Text>
							<Button
								icon={<UploadOutlined />}
								type="primary"
								onClick={() => setIsKeyenceModalOpen(true)}
							>
								Upload Programs
							</Button>
						</Flex>
					)}
					{/* Sección FAI CSV */}
					<Flex vertical align="start">
						<Text>FAI CSV</Text>
						<Flex gap="small">
							<Upload
								multiple={false}
								showUploadList={false}
								beforeUpload={() => false}
								accept=".csv"
								onChange={(info) => handleUploadFaiCSV(info.file)}
								disabled={props.isDisabled || props.uploadDisabled || csvUploading}
							>
								<Button
									icon={<UploadOutlined />}
									loading={csvUploading}
									disabled={props.isDisabled || props.uploadDisabled || csvUploading}
								>
									Upload
								</Button>
							</Upload>
							<Button
								icon={<DownloadOutlined />}
								onClick={handleCSVExport}
								loading={csvExportLoading}
								disabled={props.isDisabled || !!csvError}
							>
								Download
							</Button>
						</Flex>
						{csvError && (
							<Text type="danger" style={{ marginTop: 4 }}>
								Error exporting CSV
							</Text>
						)}
					</Flex>
					{/* Sección FAI Report */}
					<Flex vertical align="start">
						<Text>FAI Report</Text>
						<Button
							icon={<DownloadOutlined />}
							type="primary"
							onClick={handleDownloadFaiReport}
							loading={faiLoading}
							disabled={props.isDisabled || !!faiError}
						>
							{faiLoading ? 'Generating...' : 'Download'}
						</Button>
						{faiError && (
							<Text type="danger" style={{ marginTop: 4 }}>
								Error generating report
							</Text>
						)}
					</Flex>
				</Flex>
			</Flex>

			{contextHolder}
			<MultiViewUploadModal
				visible={isKeyenceModalOpen}
				onClose={() => setIsKeyenceModalOpen(false)}
				projectId={getInspectionProjectId(props.lineItem)!}
				onUploaded={props.refetchOrder}
			/>
		</>
	);
};
